import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useGetAgreementFinancialReport } from "../../services/agreementsService";
import TableService from "../../services/tableService";

import { useAgreementFinancialReportTableConfig, AGREEMENT_FINANCIAL_REPORT_TABLE_NAME } from "./TableConfig";;
import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import FinancialReportsNavigationBar from "./FinancialReportsNavigationBar";

export default function AgreementFinancialReportPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.ADMIN, USER_ROLES.INVESTMENT_DPI, USER_ROLES.INVESTMENT_PM, USER_ROLES.CONTROLING]);

    if (!hasAccess){
        return null
    }

    const { pageName } = props;

    const { t } = useTranslation();
    const TABLE_FIELDS_CONFIG = useAgreementFinancialReportTableConfig();
    
    const TABLE_CONFIGS = [{ name: AGREEMENT_FINANCIAL_REPORT_TABLE_NAME, config: TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: backendData, refetch, isLoading } = useGetAgreementFinancialReport(searchParams);
    useEffect(() => {
        if (backendData?.results) {
            setDataLocal(backendData.results);
            setRecordsCount(backendData.count);
        }
    }, [backendData, dataPage, dataPageSize]);

    const dataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME] || [],
        );
    }, [TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME]
                    : [],
            ),
        [
            TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
        ],
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[AGREEMENT_FINANCIAL_REPORT_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={false}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"agreements/financial-report/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={dataExportHeaders}
            exportToFileFileName={`${t("table.agreements.exportAgreementFinancialReportFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            hideFiltersIconCol={true}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.financial_reports")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    <FinancialReportsNavigationBar />
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                        extraButtonList={[
                            <InvestmentFilter
                                filterName={"investment"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                                InputLabelStyle={{marginTop: "-6px"}}
                                maxHeight={"40px"}
                            />]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>
                        {tableComponent()}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}